import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllFieldRequests, deleteFieldRequest, saveFieldRequest } from '../utils/offlineCache';
import { getTickets } from '../utils/api';
import DropdownSelect from '../components/DropdownSelect';

const STATUS_LABELS = {
  triaged: 'Triage done',
  triage_complete: 'Triage done',
  evidence_collecting: 'Collecting evidence',
  evidence_collection: 'Collecting evidence',
  evidence_submitted: 'Evidence submitted',
  escalation_pending: 'Awaiting approval',
  escalated: 'Awaiting approval',
  more_info_requested: 'More info requested',
  completed: 'Completed',
  closed: 'Closed',
  approved: 'Approved',
  rejected: 'Rejected',
  pending_sync: 'Pending sync',
  synced: 'Synced',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'triaged', label: 'Triage done' },
  { value: 'evidence_collecting', label: 'Collecting evidence' },
  { value: 'evidence_submitted', label: 'Evidence submitted' },
  { value: 'escalated', label: 'Awaiting approval' },
  { value: 'more_info_requested', label: 'More info requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
  { value: 'pending_sync', label: 'Pending sync' },
  { value: 'synced', label: 'Synced' },
];

const AWAITING_APPROVAL_STATUSES = ['escalation_pending', 'escalated'];

function FieldEngineerDashboard({ isOnline: isOnlineProp }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const isOnline = isOnlineProp !== undefined ? isOnlineProp : (typeof navigator !== 'undefined' && navigator.onLine);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const localList = await getAllFieldRequests();

      if (isOnline) {
        try {
          const res = await getTickets({ limit: 100 });
          const serverTickets = res.tickets || [];

          const localMap = {};
          localList.forEach((r) => { localMap[r.ticket_id] = r; });

          for (const t of serverTickets) {
            if (localMap[t.ticket_id]) {
              localMap[t.ticket_id] = {
                ...localMap[t.ticket_id],
                status: t.status,
                triage_risk_level: t.triage_risk_level,
                triage_category: t.triage_category,
                triage_confidence: t.triage_confidence,
                approval_action: t.approval_action,
                approved_by: t.approved_by,
                approved_at: t.approved_at,
                approval_notes: t.approval_notes,
              };
              await saveFieldRequest(localMap[t.ticket_id]).catch(() => {});
            }
          }

          const merged = Object.values(localMap);
          merged.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
          setRequests(merged);
        } catch {
          setRequests(localList);
        }
      } else {
        setRequests(localList);
      }
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [isOnline]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const filteredRequests = requests.filter((req) => {
    const reqStatus = req.status || '';
    const matchStatus =
      !statusFilter ||
      reqStatus === statusFilter ||
      (statusFilter === 'escalated' && AWAITING_APPROVAL_STATUSES.includes(reqStatus));
    const q = (searchFilter || '').trim().toLowerCase();
    const matchSearch =
      !q ||
      (req.ticket_id || '').toLowerCase().includes(q) ||
      (req.fault_code || '').toLowerCase().includes(q) ||
      (req.equipment_model || '').toLowerCase().includes(q) ||
      (req.symptoms || '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Remove this request from your list?')) return;
    try {
      await deleteFieldRequest(ticketId);
      setRequests((prev) => prev.filter((r) => r.ticket_id !== ticketId));
    } catch {
      // ignore
    }
  };

  return (
    <div className="page-content page-content--mobile">
      <div className="field-dashboard">
        <h2 className="field-dashboard__title">My Requests</h2>
        <p className="field-dashboard__subtitle">
          {isOnline ? 'Showing latest status from the server.' : 'Showing locally saved requests (offline).'}
        </p>

        {!loading && requests.length > 0 && (
          <div className="field-dashboard__filters">
            <DropdownSelect
              id="filter-status"
              className="field-dashboard__filter-dropdown"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              placeholder="All statuses"
            />
            <input
              type="search"
              className="field-dashboard__filter-search"
              placeholder="Search ticket, fault code, equipment…"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              aria-label="Search requests"
            />
          </div>
        )}

        {loading ? (
          <div className="field-dashboard__loading">
            <div className="loading-bar loading-bar--indeterminate" style={{ marginBottom: 16 }}>
              <div className="loading-bar__track">
                <div className="loading-bar__fill" />
              </div>
            </div>
            Loading requests…
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="field-dashboard__empty card">
            <p className="field-dashboard__empty-text">
              {requests.length === 0 ? 'No requests yet.' : 'No requests match the filter.'}
            </p>
            <p className="field-dashboard__empty-hint">
              {requests.length === 0
                ? 'Submit a ticket from the Field flow to see it here.'
                : 'Try a different status or search.'}
            </p>
            {requests.length === 0 ? (
              <Link to="/field" className="btn btn--primary">
                Go to Field
              </Link>
            ) : (
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => { setStatusFilter(''); setSearchFilter(''); }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <ul className="field-dashboard__list">
            {filteredRequests.map((req) => {
              const riskClass = (req.triage_risk_level || 'medium').toLowerCase();
              const hasApproval = req.approval_action && req.approval_action !== 'more_info_requested';
              const needsAction = req.status === 'more_info_requested';
              return (
                <li key={req.ticket_id} className="field-dashboard__item">
                  <div
                    className="field-dashboard__card card"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/field/requests/${req.ticket_id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/field/requests/${req.ticket_id}`)}
                    style={{
                      cursor: 'pointer',
                      borderLeft: needsAction
                        ? '4px solid var(--color-warning)'
                        : hasApproval
                        ? `4px solid ${req.approval_action === 'approved' ? 'var(--color-safe)' : 'var(--color-danger)'}`
                        : undefined,
                    }}
                  >
                    <div className="field-dashboard__row">
                      <span className="field-dashboard__id">{req.ticket_id}</span>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {req.triage_risk_level && (
                          <span className={`badge badge--${riskClass}`} style={{ fontSize: 10, padding: '2px 8px' }}>
                            {req.triage_risk_level}
                          </span>
                        )}
                        <span className={`field-dashboard__badge field-dashboard__badge--${(req.status || '').replace(/_/g, '-')}`}>
                          {STATUS_LABELS[req.status] || req.status}
                        </span>
                      </div>
                    </div>
                    <div className="field-dashboard__meta">
                      <span className="field-dashboard__fault">{req.fault_code}</span>
                      {req.equipment_model && (
                        <span className="field-dashboard__equipment"> · {req.equipment_model}</span>
                      )}
                      {req.triage_category && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {' · '}{(req.triage_category || '').replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    {hasApproval && (
                      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: req.approval_action === 'approved' ? 'var(--color-safe)' : 'var(--color-danger)' }}>
                        {req.approval_action === 'approved' ? '✓ Approved' : '✗ Rejected'}
                        {req.approved_by && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> by {req.approved_by}</span>}
                      </div>
                    )}
                    {needsAction && (
                      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: 'var(--color-warning)' }}>
                        Action needed: supervisor requested more info
                      </div>
                    )}
                    <div className="field-dashboard__date">
                      {formatDate(req.created_at)}
                    </div>
                    <button
                      type="button"
                      className="field-dashboard__delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDelete(req.ticket_id); }}
                      aria-label={`Delete request ${req.ticket_id}`}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="field-dashboard__actions">
          <Link to="/field" className="btn btn--outline btn--block">
            New ticket
          </Link>
          {isOnline && !loading && (
            <button
              type="button"
              className="btn btn--outline btn--block"
              onClick={loadRequests}
              style={{ marginTop: 8 }}
            >
              Refresh
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FieldEngineerDashboard;
